import CheckboxTile from '@ramsey-design-system/checkbox-tile';
import { useWizard } from 'react-use-wizard';
import { FieldError, FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { genericCheckboxSchema } from '../../schemas/genericCheckboxSchema';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../utils';
import { ReferralFormData, useReferralContext } from '../../context';
import { useGAFormTracking } from '../../../../shared/hooks';
import { JSX } from 'react';
import { trackStepCompleted } from '../../../../shared/analytics';
import { normalizeStepLabel } from '../../../../shared/utils';
import {
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../stepComponents';
import { StandardNextButton } from '../buttons';
import { ErrorText } from '../ErrorText';

interface GenericCheckboxStepFields {
  [key: string]: boolean | undefined;
}

export interface CheckboxOption {
  label: string;
  value: string;
}

interface CheckBoxOptions {
  [key: string]: CheckboxOption;
}

interface GenericCheckboxStepProps {
  title: string;
  subtitle: string;
  checkboxOptions: CheckBoxOptions;
  referralFieldName: keyof ReferralFormData;
  stepName?: string;
  additionalComponent?: JSX.Element;
}

function getObjectValue(obj: object, fieldName: string) {
  const entries = Object.entries(obj);
  const [, entryValue] = entries.find(([field]) => field === fieldName) ?? [];
  return entryValue;
}

function getSelectionIsRequiredErrorText(
  errors: FieldErrors<GenericCheckboxStepFields>
) {
  const baseShapeError = getObjectValue(errors, '') as FieldError | undefined;
  return baseShapeError?.message;
}

function getCheckboxAnswers(
  values: GenericCheckboxStepFields,
  checkboxOptions: CheckBoxOptions
) {
  return Object.entries(values)
    .filter(([, isChecked]) => isChecked)
    .map(([answer]) => answer)
    .map((answer) => checkboxOptions[answer].value);
}

function extractGenericCheckboxStepFieldsFromReferral(
  checkboxOptions: CheckBoxOptions,
  referral: ReferralFormData,
  referralFieldName: string
) {
  function accumulateCheckboxStates(
    acc: GenericCheckboxStepFields,
    checkbox: string
  ) {
    const foundCheckboxesArrayFromReferral = (getObjectValue(
      referral,
      referralFieldName
    ) ?? []) as string[];

    acc[checkbox] = foundCheckboxesArrayFromReferral.includes(
      checkboxOptions[checkbox].value
    );
    return acc;
  }
  return Object.keys(checkboxOptions).reduce(
    accumulateCheckboxStates,
    {} as GenericCheckboxStepFields
  );
}

function GenericCheckboxStep({
  title,
  subtitle,
  checkboxOptions,
  referralFieldName,
  stepName = `${referralFieldName}_checkbox_step`,
  additionalComponent = <></>
}: Readonly<GenericCheckboxStepProps>) {
  const formRef = useGAFormTracking(stepName);
  const { nextStep, activeStep } = useWizard();
  const { referral, updateReferral } = useReferralContext();
  const stepLabel = normalizeStepLabel(title);

  useTrackStepStarted(activeStep + 1, stepName, stepLabel);
  const trackError = useTrackStepError(activeStep + 1, stepName, stepLabel);

  const { register, handleSubmit, formState } =
    useForm<GenericCheckboxStepFields>({
      defaultValues: extractGenericCheckboxStepFieldsFromReferral(
        checkboxOptions,
        referral,
        referralFieldName
      ),
      resolver: yupResolver(genericCheckboxSchema)
    });
  const errorText = getSelectionIsRequiredErrorText(formState.errors);

  async function onValidSubmit(values: GenericCheckboxStepFields) {
    const answers = getCheckboxAnswers(values, checkboxOptions);
    updateReferral({
      ...referral,
      [referralFieldName]: answers
    });

    trackStepCompleted({
      step: activeStep + 1,
      step_name: stepName,
      step_label: stepLabel,
      answer_labels: Object.values(checkboxOptions).map((opt) => opt.label),
      answer_values: answers,
      form_name: getFormName()
    });
    await nextStep();
  }

  function onInvalid(formErrors: FieldErrors<GenericCheckboxStepFields>) {
    trackError('validation', getSelectionIsRequiredErrorText(formErrors));
  }

  return (
    <StepWrapper>
      <form ref={formRef} onSubmit={handleSubmit(onValidSubmit, onInvalid)}>
        <StandardStepTitle>{title}</StandardStepTitle>
        <StandardStepSubtitleText>{subtitle}</StandardStepSubtitleText>
        <div className="mb-4 space-y-1.5">
          {Object.entries(checkboxOptions)?.map((entry) => {
            const key = entry[0];
            const option = entry[1];
            return (
              <CheckboxTile
                key={key}
                {...register(`${key}`)}
                name={`${key}`}
                label={option.label}
                data-testid={`${key}_checkbox`}
                id={`${key}`}
                size="sm"
              />
            );
          })}
          <ErrorText errorText={errorText} />
        </div>

        <StandardNextButton />
      </form>
      {additionalComponent}
    </StepWrapper>
  );
}

export { GenericCheckboxStep };
